/**
 * One entry per chart or table on /dashboard/qxo.
 * `rows` is the full series (not the thinned axis the overview chart draws).
 */

import { buildDilutionLadder } from '@/components/qxo/qxoCapitalMath';
import {
  BEACON_LIKE_FOR_LIKE,
  BEACON_QUARTERS,
  DEAL_MULTIPLES,
  DEBT_STACK,
  EQUITY_LADDER,
  MANAGEMENT_PROOF_POINTS,
  PLATFORM_BASELINE,
  PREFERRED_CASH_DIVIDENDS,
  PREFERRED_STACK,
  PRODUCT_LINES,
  PRO_FORMA_FY25,
  QXO_QUARTERS,
  WORKING_CAPITAL,
} from '@/components/qxo/qxoFilingsData';
import {
  LONG_BRANCH_COST,
  LONG_CONSTRUCTION_FUNNEL,
  LONG_CONSTRUCTION_LABOR,
  LONG_EXISTING_HOME_SALES,
  LONG_HOUSING_BACKLOG,
  LONG_HOUSING_STARTS,
  LONG_INPUT_PPI,
  LONG_MORTGAGE,
  LONG_NEW_HOME_MOS,
  LONG_PUT_IN_PLACE,
  LONG_REGIONAL_STARTS,
  LONG_SF_FUNNEL,
  LONG_STARTS_VS_SALES,
  LONG_STRUCTURE_STARTS,
} from '@/components/qxo/qxoLongHistory';
import {
  ARMA_QUARTERLY,
  BLDR_CORE_ORGANIC,
  EXISTING_HOME_SALES,
  HAIL_SPC_ONE_INCH,
  IBP_CONSOL_SB,
  IBP_RESI_SB,
  MILL_VS_BRANCH,
  MORTGAGE_LATEST,
  OC_CHANNEL,
  OC_ROOFING,
} from '@/components/qxo/qxoRoofingCharts';
import {
  ARMA_STEEP_VS_LOWSLOPE,
  BLDR_END_MARKET,
  IBP_INSTALL_SPLIT,
  REPORTED_MIX,
  TOPBUILD_FY25_SB,
  TOPBUILD_SB,
} from '@/components/qxo/qxoRoofingStack';
import { quarterLabel, yearLabel, type StreetSnapshot } from '@/components/qxo/qxoStreet';

export type DatasetBoard = 'market' | 'universe' | 'filings' | 'capital' | 'street';

export interface DatasetColumn {
  key: string;
  header: string;
  align?: 'left' | 'right';
}

export interface DatasetDef {
  id: string;
  board: DatasetBoard;
  title: string;
  /** What the series actually measures. */
  represents: string;
  /** Why a QXO investor should care. */
  relatesToQxo: string;
  source: string;
  coverage: string;
  columns: DatasetColumn[];
  rows: Record<string, string>[];
}

const num = (v: number | null | undefined, digits = 1): string =>
  v == null || Number.isNaN(v) ? '—' : v.toFixed(digits);

const money = (v: number | null | undefined): string => (v == null ? '—' : `$${v.toLocaleString('en-US')}`);

function cols(headers: string[], aligns?: ('left' | 'right')[]): DatasetColumn[] {
  return headers.map((header, i) => ({
    key: `c${i}`,
    header,
    align: aligns?.[i] ?? (i === 0 ? 'left' : 'right'),
  }));
}

function row(cells: (string | number | null | undefined)[]): Record<string, string> {
  const out: Record<string, string> = {};
  cells.forEach((cell, i) => {
    out[`c${i}`] = cell == null || cell === '' ? '—' : String(cell);
  });
  return out;
}

const MARKET: DatasetDef[] = [
  {
    id: 'arma-shipments',
    board: 'market',
    title: 'Shingles leaving U.S. mills',
    represents:
      'U.S. asphalt shingle shipments reported by the Asphalt Roofing Manufacturers Association, in millions of squares (one square covers 100 square feet). This is factory output, including product that lands in distributor yards, not what contractors installed.',
    relatesToQxo:
      'Beacon’s residential book is mostly steep-slope shingles bought from these mills. If mills are flat and QXO same-store sales are down, the gap is destocking or lost share at the branch, not a mill miss. Q4 2023 and Q4 2024 are residuals from the year-end total; Q4 2025 was not in the free PDF.',
    source: 'ARMA quarterly product shipment summaries',
    coverage: 'Q1 2022 – Q2 2026',
    columns: cols(['Quarter', 'Squares (M)', 'YoY %', 'How derived']),
    rows: ARMA_QUARTERLY.map((r) => row([r.period, num(r.squaresM, 2), num(r.yoy), r.derived ? 'Year-end residual' : 'ARMA print'])),
  },
  {
    id: 'mills-vs-branch',
    board: 'market',
    title: 'Mills vs QXO store sales',
    represents:
      'Shingle mill shipments versus the same quarter a year earlier, with two branch prints overlaid: Beacon’s Q2 2024 organic sales and QXO’s Q2 2026 steep-slope same-store sales.',
    relatesToQxo:
      'The only direct read on whether QXO’s stores are tracking the mills. The Q2 2026 steep-slope −5.8% is a company official quoted by Craig Webb (17 Aug 2026), not a line in the 10-Q. Beacon’s 2024 gold dot includes new branches.',
    source: 'ARMA; Beacon Q2 2024 release; Craig Webb, 17 Aug 2026',
    coverage: 'Q3 2022 – Q2 2026 (branch prints are single points)',
    columns: cols(['Quarter', 'Mill YoY %', 'Beacon organic %', 'QXO steep-slope SSS %', 'QXO overall SSS %']),
    rows: MILL_VS_BRANCH.map((r) => row([r.period, num(r.armaYoy), num(r.beaconOrganic), num(r.qxoSteepSss), num(r.qxoAllSss)])),
  },
  {
    id: 'housing-starts',
    board: 'market',
    title: 'How many homes are being started',
    represents:
      'Quarterly average of monthly housing starts, seasonally adjusted annual rate, in millions of homes. Single-family is the steep-slope roofing book; the total includes apartments.',
    relatesToQxo:
      'New single-family starts are the forward order book for residential shingles and, after TopBuild, for insulation install. A star on Q3 2026 means July–August only. Repair and reroof (the larger Beacon slice) does not show up here.',
    source: 'Census/HUD via FRED HOUST and HOUST1F',
    coverage: 'Q1 2011 – Aug 2026',
    columns: cols(['Quarter', 'Total starts (M SAAR)', 'Single-family (M SAAR)']),
    rows: LONG_HOUSING_STARTS.map((r) => row([r.period, num(r.total, 3), num(r.single, 3)])),
  },
  {
    id: 'mortgage-rate',
    board: 'market',
    title: '30-year mortgage rate',
    represents: 'Quarterly average of the Freddie Mac 30-year fixed mortgage rate. The latest weekly print is listed under the quarterly series.',
    relatesToQxo:
      'Higher rates freeze existing-home turnover, which is where a large share of reroof demand sits, and they slow single-family starts. QXO does not lend; it feels the rate through contractor orders.',
    source: 'Freddie Mac via FRED MORTGAGE30US',
    coverage: 'Q1 2011 – week of 17 Sep 2026',
    columns: cols(['Period', '30-year rate %']),
    rows: [
      ...LONG_MORTGAGE.map((r) => row([r.period, num(r.rate, 2)])),
      row([`Week of ${MORTGAGE_LATEST.date}`, num(MORTGAGE_LATEST.rate, 2)]),
    ],
  },
  {
    id: 'existing-home-sales',
    board: 'market',
    title: 'Existing homes changing hands',
    represents:
      'NAR existing-home sales, millions of homes. Annual totals through 2025; August 2026 is a seasonally adjusted annual rate, not a year-to-date count.',
    relatesToQxo:
      'Turnover triggers reroofs, insurance work, and complementary product (siding, gutters) at Beacon branches. The post-2022 drop from ~6M to ~4M sales is the backdrop for residential same-store declines.',
    source: 'National Association of Realtors, annual totals via YCharts',
    coverage: '2011 – Aug 2026',
    columns: cols(['Period', 'Sales (M)', 'Notes']),
    rows: [
      ...LONG_EXISTING_HOME_SALES.map((r) => row([r.year, num(r.salesM, 2), 'Annual total'])),
      row([
        EXISTING_HOME_SALES.month,
        num(EXISTING_HOME_SALES.saarM, 2),
        `SAAR, ${num(EXISTING_HOME_SALES.yoy)}% YoY, ${num(EXISTING_HOME_SALES.monthsSupply)} months of inventory`,
      ]),
    ],
  },
  {
    id: 'oc-roofing',
    board: 'market',
    title: 'Owens Corning roofing',
    represents:
      'Owens Corning’s Roofing segment net sales and the year-over-year change in those dollars. Volume is disclosed only when OC states it.',
    relatesToQxo:
      'OC is the largest branded shingle mill selling into distributors like Beacon. Dollar growth can be price while volume (the ARMA cousin) is down. OC’s own Q2 2026 volume was about −1% and first-half volume −7%.',
    source: 'Owens Corning filings and calls',
    coverage: 'Q1 2025 – Q2 2026',
    columns: cols(['Quarter', 'Net sales $M', 'Sales YoY %', 'Volume YoY %']),
    rows: OC_ROOFING.map((r) => row([r.period, num(r.salesM, 0), num(r.salesYoy), num(r.volYoy)])),
  },
  {
    id: 'peer-tape',
    board: 'market',
    title: 'Installer and lumber peers',
    represents:
      'Each company’s own same-branch or organic growth, not a shared definition. IBP is installation. Builders FirstSource core organic excludes commodities, acquisitions, and selling-day effects.',
    relatesToQxo:
      'IBP residential is the public stand-in for TopBuild’s install book until QXO reports it. BLDR is the stand-in for Kodiak’s lumber and building-materials branches. Do not average the lines.',
    source: 'IBP and Builders FirstSource earnings releases',
    coverage: 'Q4 2025 – Q2 2026 where verified; IBP from Q1 2025',
    columns: cols(['Quarter', 'IBP residential %', 'IBP company %', 'BLDR core organic %']),
    rows: (() => {
      const periods = Array.from(
        new Set([...IBP_RESI_SB.map((r) => r.period), ...BLDR_CORE_ORGANIC.map((r) => r.period)]),
      );
      return periods.map((period) =>
        row([
          period,
          num(IBP_RESI_SB.find((r) => r.period === period)?.yoy),
          num(IBP_CONSOL_SB.find((r) => r.period === period)?.yoy),
          num(BLDR_CORE_ORGANIC.find((r) => r.period === period)?.yoy),
        ]),
      );
    })(),
  },
  {
    id: 'oc-channel',
    board: 'market',
    title: 'Owens Corning on the channel',
    represents:
      'Qualitative channel comments from OC’s Q2 2026 call, plus NOAA Storm Prediction Center counts of 1-inch hail events. Hail counts are storms, not squares of shingles.',
    relatesToQxo:
      'OC described distributor inventories as a little heavier than normal and storm demand as regional. Hail events in 2025 were almost the same as 2024, so the mill slowdown is not a hail story. That pushes the explanation toward price, destock, and housing.',
    source: 'OC Q2 2026 call; SPC via Insurance Information Institute',
    coverage: 'Q2 2026 commentary; hail 2024–2025',
    columns: cols(['Item', 'Detail'], ['left', 'left']),
    rows: [
      row(['Stronger regions', OC_CHANNEL.regionsGood]),
      row(['Weaker regions', OC_CHANNEL.regionsWeak]),
      row(['Distributor inventory', OC_CHANNEL.inventory]),
      row(['Storm carryover', OC_CHANNEL.stormCarryover]),
      row(['OC guide for ARMA Q3 2026', OC_CHANNEL.q3ArmaGuide]),
      ...HAIL_SPC_ONE_INCH.map((r) => row([`${r.year} 1-inch hail events`, r.events.toLocaleString()])),
    ],
  },
  {
    id: 'construction-funnel',
    board: 'market',
    title: 'Permits, starts, finished homes',
    represents:
      'The same Census survey at three stages: permits authorized, homes started, homes completed. All are seasonally adjusted annual rates, millions of units.',
    relatesToQxo:
      'Completions are what installer crews (TopBuild, IBP) feel. Starts lead shingle and lumber orders. Q3 2026 is July–August only. Completions rolling over hits jobsites after starts already slowed.',
    source: 'FRED PERMIT, HOUST, COMPUTSA',
    coverage: 'Q1 2011 – Aug 2026',
    columns: cols(['Quarter', 'Permits (M)', 'Starts (M)', 'Completions (M)']),
    rows: LONG_CONSTRUCTION_FUNNEL.map((r) => row([r.period, num(r.permit, 3), num(r.starts, 3), num(r.complete, 3)])),
  },
  {
    id: 'regional-starts',
    board: 'market',
    title: 'Where homes are being started',
    represents: 'Housing starts by Census region, millions, seasonally adjusted annual rate.',
    relatesToQxo:
      'The South is the storm and Sunbelt roofing book. The Midwest is the pocket Owens Corning called out as holding up. A national average hides a weak Southeast against a firmer Midwest, which is how Beacon’s branch map actually works.',
    source: 'FRED HOUSTS, HOUSTMW, HOUSTW, HOUSTNE',
    coverage: 'Q1 2011 – Aug 2026',
    columns: cols(['Quarter', 'South', 'Midwest', 'West', 'Northeast']),
    rows: LONG_REGIONAL_STARTS.map((r) => row([r.period, num(r.south, 3), num(r.midwest, 3), num(r.west, 3), num(r.ne, 3)])),
  },
  {
    id: 'steep-vs-low',
    board: 'market',
    title: 'Steep-slope vs low-slope mill shipments',
    represents:
      'Year-over-year change in ARMA shipments: U.S. shingles (steep-slope, residential) versus U.S. modified bitumen (low-slope, commercial). Only quarters where both prints exist.',
    relatesToQxo:
      'Beacon sells both. Steep-slope is the residential same-store line management quoted at −5.8% in Q2 2026; low-slope was −1.3%. When mod-bit and shingles diverge, mix — not just “roofing” — is moving.',
    source: 'ARMA quarterly summaries',
    coverage: 'Selected quarters Q2 2023 – Q2 2026',
    columns: cols(['Quarter', 'Shingles YoY %', 'Modified bitumen YoY %']),
    rows: ARMA_STEEP_VS_LOWSLOPE.map((r) => row([r.period, num(r.shingles), num(r.modbit)])),
  },
  {
    id: 'input-ppi',
    board: 'market',
    title: 'Mill input, shingle price, lumber',
    represents:
      'BLS producer prices, rebased so Q1 2019 = 100. Asphalt is what mills pay. Shingles are what they charge. Lumber is the Kodiak book. These are indexes, not dollar prices.',
    relatesToQxo:
      'Gross margin at a distributor is the gap between what mills charge and what branches charge contractors, plus vendor rebates. Asphalt ripping higher while shingle prices lag is a squeeze that shows up one quarter later in adjusted gross margin.',
    source: 'BLS via FRED WPU0576, PCU324122324122, WPU081',
    coverage: 'Q1 2011 – Aug 2026',
    columns: cols(['Quarter', 'Asphalt (Q1 19=100)', 'Shingles', 'Lumber']),
    rows: LONG_INPUT_PPI.map((r) => row([r.period, num(r.asphalt), num(r.shingles), num(r.lumber)])),
  },
  {
    id: 'installer-split',
    board: 'market',
    title: 'Installer jobs: houses vs commercial',
    represents:
      'Same-branch sales growth for installers. IBP splits residential and commercial. TopBuild splits residential and commercial/industrial, only in quarters it published the split.',
    relatesToQxo:
      'TopBuild closed 1 July 2026 and is QXO’s insulation-install platform. Its FY2025 same-branch was residential −10.8% and commercial/industrial −0.9%. IBP is the live public proxy until QXO prints TopBuild organic.',
    source: 'IBP and TopBuild earnings releases',
    coverage: 'IBP Q1 2025 – Q2 2026; TopBuild Q3–Q4 2025 and FY2025',
    columns: cols(['Period', 'IBP residential %', 'IBP commercial %', 'TopBuild residential %', 'TopBuild C/I %']),
    rows: [
      ...IBP_INSTALL_SPLIT.map((r) => {
        const tb = TOPBUILD_SB.find((t) => t.period === r.period);
        return row([r.period, num(r.resi), num(r.comm), num(tb?.resi), num(tb?.ci)]);
      }),
      row(['FY 2025 TopBuild', '—', '—', num(TOPBUILD_FY25_SB.resi), num(TOPBUILD_FY25_SB.ci)]),
    ],
  },
  {
    id: 'bldr-end-market',
    board: 'market',
    title: 'Kodiak analog: end market',
    represents:
      'Builders FirstSource core organic growth by end market: single-family, multifamily, and repair & remodel. Q1 2026 company-wide core organic was not disclosed.',
    relatesToQxo:
      'Kodiak is lumber and building materials, a different cycle from shingles. BLDR’s repair book has been firmer than new construction. That is the read on whether Kodiak’s complementary line can offset a weak residential roofing quarter.',
    source: 'Builders FirstSource earnings releases',
    coverage: 'Q4 2025 – Q2 2026',
    columns: cols(['Quarter', 'Single-family %', 'Multifamily %', 'Repair & remodel %', 'Core organic %']),
    rows: BLDR_END_MARKET.map((r) => row([r.period, num(r.sf), num(r.mf), num(r.rr), num(r.core)])),
  },
  {
    id: 'reported-mix',
    board: 'market',
    title: 'Reported sales mix',
    represents:
      'Share of net sales in residential roofing, non-residential roofing, and complementary products. Three snapshots, not a continuous series.',
    relatesToQxo:
      'Q2 2024 is Beacon alone. Q2 2025 is QXO with two months of Beacon. Q2 2026 includes a full quarter of Kodiak, so complementary jumps because lumber arrived, not because Beacon’s mix improved.',
    source: 'Beacon Q2 2024 release; QXO Q2 2025 and Q2 2026 releases',
    coverage: 'Three quarters',
    columns: cols(['Snapshot', 'Residential %', 'Non-residential %', 'Complementary %']),
    rows: REPORTED_MIX.map((r) => row([r.period, num(r.resi), num(r.nonresi), num(r.complementary)])),
  },
  {
    id: 'structure-starts',
    board: 'market',
    title: 'Houses vs apartments',
    represents:
      'Census starts split into 1-family houses and buildings with 5 or more units. 2–4 unit starts are omitted (about 13,000 in Aug 2026). Millions, SAAR.',
    relatesToQxo:
      'Houses take steep-slope shingles and a TopBuild insulation package. Apartments take low-slope roofing and a different install job. The mix between the two changes what QXO sells even when total starts look stable.',
    source: 'FRED HOUST1F, HOUST5F',
    coverage: 'Q1 2011 – Aug 2026',
    columns: cols(['Quarter', 'Single-family (M)', '5+ unit (M)']),
    rows: LONG_STRUCTURE_STARTS.map((r) => row([r.period, num(r.sf, 3), num(r.mf, 3)])),
  },
  {
    id: 'sf-funnel',
    board: 'market',
    title: 'Single-family pipeline',
    represents: 'Permits, starts, and completions for 1-family homes only. Same Census units as the total funnel.',
    relatesToQxo:
      'This is the housing slice that feeds Beacon residential and TopBuild residential install. Completions falling faster than starts means crews have less work even if permits look steady.',
    source: 'FRED PERMIT1, HOUST1F, COMPU1USA',
    coverage: 'Q1 2011 – Aug 2026',
    columns: cols(['Quarter', 'SF permits (M)', 'SF starts (M)', 'SF completions (M)']),
    rows: LONG_SF_FUNNEL.map((r) => row([r.period, num(r.permit, 3), num(r.starts, 3), num(r.complete, 3)])),
  },
  {
    id: 'starts-vs-sales',
    board: 'market',
    title: 'Houses started vs houses sold',
    represents: '1-family starts versus new 1-family homes sold. Both Census, millions, SAAR.',
    relatesToQxo:
      'Builders start homes before they sell them. When starts run ahead of sales, builder inventory piles up and later starts get cut — the order book for lumber (Kodiak) and shingles (Beacon) follows with a lag.',
    source: 'FRED HOUST1F, HSN1F',
    coverage: 'Q1 2011 – Jul 2026 (Q3 2026 sales are July)',
    columns: cols(['Quarter', 'SF starts (M)', 'New homes sold (M)']),
    rows: LONG_STARTS_VS_SALES.map((r) => row([r.period, num(r.starts, 3), num(r.sold, 3)])),
  },
  {
    id: 'new-home-mos',
    board: 'market',
    title: 'Months to sell new-home inventory',
    represents: 'Census months of supply of new single-family homes (MSACSR). How long the current inventory would last at the current sales pace.',
    relatesToQxo:
      'Above ~6 months, builders slow starts. That is the leading indicator for Kodiak lumber and Beacon new-construction roofing, distinct from the repair book.',
    source: 'FRED MSACSR',
    coverage: 'Q1 2011 – Jul 2026',
    columns: cols(['Quarter', 'Months of supply']),
    rows: LONG_NEW_HOME_MOS.map((r) => row([r.period, num(r.mos, 2)])),
  },
  {
    id: 'housing-backlog',
    board: 'market',
    title: 'Homes already in the pipeline',
    represents:
      'Homes under construction (seasonally adjusted) and homes authorized but not yet started. Levels in millions, not annual rates.',
    relatesToQxo:
      'Under-construction is work already in the field for installers. Authorized-not-started is the backlog that can still be cancelled if rates stay high. Both sit between a permit and a shingle order.',
    source: 'FRED UNDCONTSA, AUTHNOTT',
    coverage: 'Q1 2011 – Aug 2026',
    columns: cols(['Quarter', 'Under construction (M)', 'Authorized, not started (M)']),
    rows: LONG_HOUSING_BACKLOG.map((r) => row([r.period, num(r.under, 3), num(r.notStarted, 3)])),
  },
  {
    id: 'put-in-place',
    board: 'market',
    title: 'Construction dollars put in place',
    represents:
      'Census C30 private construction spending, billions of dollars, seasonally adjusted annual rate. Residential versus nonresidential.',
    relatesToQxo:
      'Dollar spending is what contractors actually buy against. Residential dollars are the steep-slope and single-family book; nonresidential dollars are low-slope and TopBuild commercial. Volume can fall while dollars hold up if prices rose.',
    source: 'FRED PRRESCONS, PNRESCONS',
    coverage: 'Q1 2011 – Jul 2026',
    columns: cols(['Quarter', 'Residential $B SAAR', 'Nonresidential $B SAAR']),
    rows: LONG_PUT_IN_PLACE.map((r) => row([r.period, num(r.resi, 1), num(r.nonres, 1)])),
  },
  {
    id: 'branch-cost',
    board: 'market',
    title: 'What it costs a branch to move product',
    represents:
      'Diesel and truck-freight producer prices, rebased to Q1 2019 = 100. This is yard and delivery cost, not the shingle price index.',
    relatesToQxo:
      'Beacon’s margin story is not only product cost. Delivery and yard diesel hit SG&A and gross margin on delivered orders. The 2026 diesel spike is a cost QXO did not have in Beacon’s 2024 cost base.',
    source: 'BLS via FRED WPU057303, WPU3012',
    coverage: 'Q1 2011 – Aug 2026',
    columns: cols(['Quarter', 'Diesel (Q1 19=100)', 'Truck freight']),
    rows: LONG_BRANCH_COST.map((r) => row([r.period, num(r.diesel), num(r.freight)])),
  },
  {
    id: 'construction-labor',
    board: 'market',
    title: 'Construction payrolls',
    represents:
      'Year-over-year change in construction employment and in JOLTS construction job openings. Payrolls move slower than openings.',
    relatesToQxo:
      'Installer capacity is the constraint on TopBuild and on Beacon’s contractor customers. Openings cooling before payrolls means crews are still on site but hiring has stopped — the next step is fewer jobs, then less material.',
    source: 'BLS via FRED USCONS, JTS2300JOL',
    coverage: 'Q1 2012 – Aug 2026 (a year of history is used up by the year-ago comparison; openings lag a month)',
    columns: cols(['Quarter', 'Payrolls YoY %', 'Job openings YoY %']),
    rows: LONG_CONSTRUCTION_LABOR.map((r) => row([r.period, num(r.empYoy), num(r.openYoy)])),
  },
];

const FILINGS: DatasetDef[] = [
  {
    id: 'beacon-margins',
    board: 'filings',
    title: 'Beacon margins before and after the takeover',
    represents:
      'Adjusted EBITDA margin and net sales. Gold points are Beacon reporting for itself. Pink points are QXO reporting after it owned the branches. Q2 2025 is omitted because QXO only owned Beacon for 63 days.',
    relatesToQxo:
      'This is the cleanest test of the 2030 plan. Beacon printed 9.5% for FY2024 and 11.7% in its best quarter. The 2030 Beacon-platform target needs about 21% on flat sales. The series since the close is the evidence so far.',
    source: 'Beacon FY2024 8-Ks; QXO earnings releases Q3 2025 – Q2 2026; predecessor stub in QXO 8-K/A',
    coverage: 'Q1 2024 – Q2 2026',
    columns: cols(['Period', 'Who filed', 'Net sales $M', 'Adj. EBITDA margin %']),
    rows: [
      ...BEACON_QUARTERS.filter((q) => q.period.startsWith('Q')).map((q) =>
        row([q.period, 'Beacon', num(q.netSales, 1), num(q.adjEbitdaMarginPct)]),
      ),
      row(['Jan–Apr 2025 stub', 'Beacon predecessor', '2694.6', '5.0']),
      ...QXO_QUARTERS.filter((q) => q.period !== 'Q2 25').map((q) =>
        row([q.period, 'QXO', num(q.netSales, 1), num(q.adjEbitdaMarginPct)]),
      ),
    ],
  },
  {
    id: 'like-for-like',
    board: 'filings',
    title: 'Same quarter, same branches',
    represents:
      'Beacon-perimeter net sales versus the same quarter a year earlier, with legacy QXO software and Kodiak stripped out so the branches match.',
    relatesToQxo:
      'Reported QXO growth is mostly acquisitions. This table is the organic read on the asset Jacobs paid 11.4x for. Sales are down in each of the last four comparable quarters.',
    source: 'Derived from Beacon and QXO filings; method in docs/QXO_FILINGS_STREET_DESK.md',
    coverage: 'Q3 2025 – Q2 2026 vs year-ago Beacon',
    columns: cols(['Compare', 'Beacon year ago $M', 'Same branches $M', 'Sales YoY %', 'Margin then %', 'Margin now %', 'EBITDA YoY %']),
    rows: BEACON_LIKE_FOR_LIKE.map((r) =>
      row([
        r.period,
        num(r.beaconYearAgoSales, 1),
        num(r.qxoPerimeterSales, 1),
        num(r.salesYoyPct),
        num(r.beaconYearAgoMarginPct),
        num(r.qxoPerimeterMarginPct),
        num(r.ebitdaYoyPct),
      ]),
    ),
  },
  {
    id: 'qxo-quarters',
    board: 'filings',
    title: 'Quarterly P&L as reported',
    represents:
      'QXO consolidated results as printed in the earnings releases. Adjusted gross margin, EBITDA, and EPS are the company’s non-GAAP figures. CFO is operating cash flow for that quarter, backed out of the year-to-date statement.',
    relatesToQxo:
      'This is the GAAP and adjusted record since Beacon closed (29 Apr 2025). Kodiak enters in Q2 2026 ($595M of sales). TopBuild is not in any of these quarters; it closed 1 Jul 2026.',
    source: 'QXO earnings releases Q2 2025 – Q2 2026',
    coverage: 'Q2 2025 – Q2 2026',
    columns: cols(['Quarter', 'Net sales $M', 'Adj. GM %', 'SG&A $M', 'Adj. EBITDA $M', 'Margin %', 'Adj. EPS', 'CFO $M', 'Capex $M']),
    rows: QXO_QUARTERS.map((r) =>
      row([
        r.period,
        num(r.netSales, 1),
        num(r.grossMarginPct),
        num(r.sgaM, 1),
        num(r.adjEbitda, 1),
        num(r.adjEbitdaMarginPct),
        r.adjDilutedEps == null ? '—' : r.adjDilutedEps.toFixed(2),
        num(r.cfoM, 1),
        num(r.capexM, 1),
      ]),
    ),
  },
  {
    id: 'beacon-standalone',
    board: 'filings',
    title: 'Beacon standalone',
    represents: 'Beacon Roofing Supply as it reported before the tender, plus the predecessor stub QXO filed for 1 Jan–28 Apr 2025.',
    relatesToQxo:
      'This is the baseline the takeout was priced on: FY2024 sales $9.76B, adjusted EBITDA $930M, 25.7% gross margin. Every “synergy” claim is a claim against these numbers.',
    source: 'Beacon 8-K earnings releases FY2024; QXO 8-K/A predecessor stub',
    coverage: 'Q1 2024 – FY 2024, plus the 2025 stub',
    columns: cols(['Period', 'Net sales $M', 'Gross margin %', 'Adj. EBITDA $M', 'EBITDA margin %']),
    rows: BEACON_QUARTERS.map((r) =>
      row([r.period, num(r.netSales, 1), num(r.grossMarginPct), num(r.adjEbitda, 1), num(r.adjEbitdaMarginPct)]),
    ),
  },
  {
    id: 'product-lines',
    board: 'filings',
    title: 'Product-line sales',
    represents:
      'Net sales split into residential roofing, non-residential roofing, and complementary. Q2 2026 complementary excludes the $595M Kodiak contribution so the line matches Beacon’s definition.',
    relatesToQxo:
      'Residential is where the decline is. Complementary ex-Kodiak is the only line up on a two-year basis. Q2 2025 and Q4 2025 were not disclosed in a comparable split, so they are absent rather than estimated.',
    source: 'Beacon 8-Ks; QXO Q3 2025, Q1 2026, Q2 2026 releases',
    coverage: 'Q1 2024 – Q2 2026, with Q2 2025 and Q4 2025 missing',
    columns: cols(['Quarter', 'Filed by', 'Residential $M', 'Non-residential $M', 'Complementary $M']),
    rows: PRODUCT_LINES.map((r) =>
      row([r.period, r.perimeter === 'QXO reported' ? 'QXO' : 'Beacon', num(r.residential, 1), num(r.nonResidential, 1), num(r.complementary, 1)]),
    ),
  },
  {
    id: 'working-capital',
    board: 'filings',
    title: 'Working capital',
    represents:
      'Trade working capital on the consolidated balance sheet: receivables, inventory, vendor rebates still owed to QXO, and payables. June 2026 includes Kodiak, not TopBuild.',
    relatesToQxo:
      'Vendor rebates receivable flow through gross margin and are a judgment-heavy asset. Net trade working capital is the cash tied up in the branches. The ~$3.1B of TopBuild escrow sitting in prepaids at 30 Jun 2026 is excluded here.',
    source: 'Balance sheets in QXO earnings releases',
    coverage: '30 Sep 2025 – 30 Jun 2026',
    columns: cols(['Date', 'Receivables $M', 'Inventory $M', 'Rebates due $M', 'Payables $M', 'Net trade WC $M', 'Cash $M']),
    rows: WORKING_CAPITAL.map((r) =>
      row([
        r.date,
        num(r.receivables, 1),
        num(r.inventory, 1),
        num(r.rebates, 1),
        num(r.payables, 1),
        num(r.receivables + r.inventory + r.rebates - r.payables, 1),
        num(r.cash, 1),
      ]),
    ),
  },
  {
    id: 'fcf-vs-pref',
    board: 'filings',
    title: 'Free cash flow vs preferred dividends',
    represents:
      'Quarterly free cash flow (operating cash flow minus capex) against preferred dividends actually paid in cash. Q2 2025 is omitted with the partial-ownership quarter.',
    relatesToQxo:
      'Preferred coupons are paid before common sees a dollar. Trailing four quarters of free cash flow roughly matched cash preferred dividends, before Series C steps up and before the new TopBuild interest bill.',
    source: 'QXO cash-flow statements',
    coverage: 'Q3 2025 – Q2 2026',
    columns: cols(['Quarter', 'CFO $M', 'Capex $M', 'Free cash flow $M', 'Preferred paid $M']),
    rows: QXO_QUARTERS.filter((q) => q.period !== 'Q2 25').map((q) => {
      const pref = PREFERRED_CASH_DIVIDENDS.find((p) => p.period === q.period);
      const fcf = (q.cfoM ?? 0) - (q.capexM ?? 0);
      return row([q.period, num(q.cfoM, 1), num(q.capexM, 1), num(fcf, 1), pref ? num(pref.paid, 1) : '—']);
    }),
  },
  {
    id: 'proof-points',
    board: 'filings',
    title: 'Management claims you can check',
    represents: 'Statements management has made on the record, and the filing line that would confirm or contradict each one.',
    relatesToQxo:
      'The 2030 plan is a set of claims. This list is the checklist for the next few 10-Qs: gross margin versus Beacon’s 25.7%, SG&A ratio, Beacon-perimeter EBITDA, and TopBuild same-branch once it is consolidated.',
    source: 'Investor day, earnings releases, 1 Jul 2026 close release',
    coverage: 'Claims through 13 Aug 2026',
    columns: cols(['Claim', 'Said', 'Check against', 'Status'], ['left', 'left', 'left', 'left']),
    rows: MANAGEMENT_PROOF_POINTS.map((p) => row([p.claim, p.saidWhen, p.checkAgainst, p.status])),
  },
];

const LADDER_PRICES = Array.from({ length: 23 }, (_, i) => 8 + i);

const CAPITAL: DatasetDef[] = [
  {
    id: 'ebitda-waterfall',
    board: 'capital',
    title: 'From EBITDA to what is left',
    represents:
      'FY2025 adjusted EBITDA of the three platforms, minus cash interest on the post-TopBuild debt stack, minus preferred dividends. What remains is before capex, cash taxes, and integration costs.',
    relatesToQxo:
      'Common shareholders are last. At the FY2025 base, fixed charges take about 40% of combined EBITDA. QXO’s Article 11 pro forma already shows a GAAP loss to common of $851M after amortization.',
    source: 'Platform FY2025 filings; Q2 2026 10-Q debt note; 9 Jul 2026 pro forma 8-K',
    coverage: 'FY2025 run-rate, post-TopBuild capital structure',
    columns: cols(['Step', '$M']),
    rows: [
      row(['Combined FY25 adj. EBITDA', num(PRO_FORMA_FY25.combinedAdjEbitda, 1)]),
      row(['Article 11 interest expense', num(PRO_FORMA_FY25.interestExpense, 1)]),
      row(['Article 11 preferred dividends', num(PRO_FORMA_FY25.preferredDividends, 1)]),
      row(['Article 11 loss to common', num(PRO_FORMA_FY25.lossToCommon, 1)]),
      row(['Article 11 amortization', num(PRO_FORMA_FY25.amortization, 1)]),
    ],
  },
  {
    id: 'debt-stack',
    board: 'capital',
    title: 'Debt stack after TopBuild',
    represents:
      'Principal, coupon, and maturity of every funded tranche after the 1 Jul 2026 close. The incremental term loan rate is an estimate until the Q3 2026 10-Q prints the effective rate.',
    relatesToQxo:
      'Nothing matures before 2030, and about 42% of principal floats with SOFR. Each 100 bp move is roughly $39M of annual interest, which comes out of the residual in the waterfall.',
    source: 'Q2 2026 10-Q Note 9; 1 Jul 2026 8-K',
    coverage: 'Balances at and immediately after 1 Jul 2026',
    columns: cols(['Tranche', 'Principal $M', 'Rate %', 'Type', 'Matures', 'Secured'], ['left', 'right', 'right', 'left', 'left', 'left']),
    rows: DEBT_STACK.map((d) => row([d.name, num(d.principalM, 0), num(d.ratePct, 2), d.rateType, d.maturity, d.secured ? 'yes' : 'no'])),
  },
  {
    id: 'preferred',
    board: 'capital',
    title: 'Preferred layers',
    represents:
      'The three preferred classes senior to common: liquidation preference, coupon, conversion price, and shares issued if converted.',
    relatesToQxo:
      'Series A (Jacobs) converts at $4.57 and already behaves like common with a 9% coupon. Series B converts mandatorily in May 2028. Series C (Apollo, Temasek) is a $3.0B claim until the stock clears $23.25.',
    source: 'Q2 2026 10-Q mezzanine equity note; Series C certificate',
    coverage: 'Outstanding at 30 Jun 2026 / 1 Jul 2026',
    columns: cols(['Layer', 'Claim $M', 'Coupon %', 'Dividend / yr $M', 'Conversion $', 'Shares if converted (M)']),
    rows: PREFERRED_STACK.map((p) =>
      row([p.name, num(p.liquidationM, 0), num(p.couponPct, 2), num(p.annualDividendM, 1), p.conversionPrice == null ? '—' : p.conversionPrice.toFixed(2), num(p.conversionSharesM, 1)]),
    ),
  },
  {
    id: 'dilution-curve',
    board: 'capital',
    title: 'Fully diluted shares vs price',
    represents:
      'Common shares outstanding after pre-funded warrants, preferred conversions, and the treasury-stock method on warrants, at each whole-dollar price from $8 to $30.',
    relatesToQxo:
      'The share count is not the 1,037.5M on the 10-Q cover. At the current price Series A and the in-the-money warrants are already economic common. The step above $23.25 is Series C converting, about 129M shares.',
    source: 'Computed from the 10-Q equity, warrant, and preferred notes (qxoCapitalMath.ts)',
    coverage: '$8 – $30 per share',
    columns: cols(['Price', 'Fully diluted (M)', 'Unconverted preferred $M']),
    rows: LADDER_PRICES.map((price) => {
      const ladder = buildDilutionLadder(price);
      return row([`$${price}`, num(ladder.fullyDilutedM, 1), num(ladder.unconvertedPreferredM, 0)]);
    }),
  },
  {
    id: 'equity-ladder',
    board: 'capital',
    title: 'Every price QXO issued stock at',
    represents:
      'Each time QXO sold or issued common: private placements, public offerings, shares issued to Kodiak and TopBuild sellers, and the Series C conversion reference.',
    relatesToQxo:
      'The value-weighted price of stock issued for cash or assets sits well above the market price. TopBuild holders took stock at an $18.44 reference; Apollo and Temasek convert at $23.25. Those holders are the overhang and the vote.',
    source: '10-Q equity note; Kodiak and TopBuild 8-Ks',
    coverage: 'Jun 2024 – Jul 2026',
    columns: cols(['When', 'Event', 'Price', 'Shares (M)', 'Value $M', 'Kind'], ['left', 'left', 'right', 'right', 'right', 'left']),
    rows: EQUITY_LADDER.map((e) => row([e.date, e.event, e.price.toFixed(2), num(e.sharesM, 1), money(e.grossM), e.kind])),
  },
  {
    id: 'deal-multiples',
    board: 'capital',
    title: 'What Jacobs paid',
    represents:
      'Enterprise value paid for each platform divided by that platform’s trailing adjusted EBITDA, using the EBITDA year cited in the filing.',
    relatesToQxo:
      'The stock’s own EV/EBITDA (on the capital board, and it moves with the price slider) is the market’s price for the same earnings stream Jacobs bought at 10.8x–13.2x. A discount to those deal multiples means the market is not paying up for the roll-up.',
    source: 'Deal 8-Ks and platform FY EBITDA as filed',
    coverage: 'Beacon Apr 2025, Kodiak Apr 2026, TopBuild Jul 2026',
    columns: cols(['Deal', 'EV $M', 'EBITDA $M', 'Multiple', 'EBITDA basis'], ['left', 'right', 'right', 'right', 'left']),
    rows: DEAL_MULTIPLES.map((d) => row([d.name, money(d.evM), num(d.ebitdaM, 1), `${(d.evM / d.ebitdaM).toFixed(1)}x`, d.basis])),
  },
  {
    id: 'platforms',
    board: 'capital',
    title: 'Three platforms: paid, earned, promised',
    represents:
      'Price paid, FY2025 sales and adjusted EBITDA, the multiple paid, and the 2030 EBITDA target for Beacon, Kodiak, and TopBuild.',
    relatesToQxo:
      'The 2030 “$4 billion organic” target is the sum of these three targets. Beacon is the one asked to more than double EBITDA. TopBuild is the only platform bought above 12x and the only one already earning an 18% margin.',
    source: 'QXO 8-K/A, S-4, and earnings releases; see PLATFORM_BASELINE sources',
    coverage: 'FY2025 actuals; 2030 targets as stated',
    columns: cols(['Platform', 'Closed', 'Price', 'FY25 sales $M', 'FY25 EBITDA $M', 'Margin %', 'Paid', '2030 target']),
    rows: PLATFORM_BASELINE.map((p) =>
      row([
        p.platform,
        p.closed,
        `$${p.priceB.toFixed(2)}B`,
        num(p.fy25Sales, 1),
        num(p.fy25AdjEbitda, 1),
        num(p.fy25MarginPct),
        `${p.paidMultiple.toFixed(1)}x`,
        `$${p.target2030EbitdaB.toFixed(1)}B`,
      ]),
    ),
  },
];

export const DATASETS: DatasetDef[] = [...MARKET, ...FILINGS, ...CAPITAL];

const STREET_META: Omit<DatasetDef, 'columns' | 'rows'>[] = [
  {
    id: 'annual-consensus',
    board: 'street',
    title: 'Annual consensus',
    represents:
      'Sell-side average revenue and EPS by fiscal year, as aggregated by Financial Modeling Prep. Contributor counts are in the table. 2029–2030 EPS often rests on one analyst.',
    relatesToQxo:
      'Management talks about $50B of revenue and $4B of EBITDA by 2030. The street’s revenue path is the independent check, and it already assumes more deals than the three that have closed. FMP’s EBITDA field is inconsistent with the quarterly path and is not treated as a forecast.',
    source: 'FMP analyst-estimates, annual',
    coverage: 'Latest FMP snapshot',
  },
  {
    id: 'quarterly-consensus',
    board: 'street',
    title: 'Quarterly consensus',
    represents: 'Average, low, and high revenue and EPS for the next several fiscal quarters.',
    relatesToQxo:
      'Q3 2026 is the first quarter with TopBuild inside the company. The seasonal dip into Q4 and Q1 is the same shape that produced about $1M of adjusted EBITDA in Q1 2026, before the new interest bill.',
    source: 'FMP analyst-estimates, quarterly',
    coverage: 'Upcoming quarters in the snapshot',
  },
  {
    id: 'prints-vs-estimates',
    board: 'street',
    title: 'Prints vs estimates',
    represents: 'Reported revenue and EPS against the consensus that was in the market, and the surprise.',
    relatesToQxo:
      'Revenue has beaten since Beacon closed. EPS has not. Beats on the top line with misses or in-line EPS means the fixed charges and mix, not demand, are what the quarter is about.',
    source: 'FMP earnings',
    coverage: 'Recent reported quarters',
  },
  {
    id: 'ownership-13f',
    board: 'street',
    title: '13F position changes',
    represents:
      'How many institutions opened, increased, cut, or closed positions in the latest 13F quarter whose filing deadline has passed. Share totals are what those filers reported, not the whole float.',
    relatesToQxo:
      'QXO’s share count changed with every deal, so a raw “ownership percent” from the data vendor can exceed 100%. Read the share change and the new-versus-closed counts. Institutions are the incremental buyer against the issuance ladder.',
    source: 'FMP institutional-ownership summary (13F)',
    coverage: 'Latest reported quarter in the snapshot',
  },
  {
    id: 'insiders',
    board: 'street',
    title: 'Insider transactions',
    represents:
      'Form 4 lines classified into open-market buys, open-market sales, tax withholding, and equity-award vesting. Only P-Purchase and S-Sale are treated as conviction.',
    relatesToQxo:
      'Awards and tax withholding are compensation, not a view on the stock. Open-market buying at a price below every issuance since 2024 would be the insider signal. The tally is in the header of the table.',
    source: 'FMP insider-trading search (SEC Form 4)',
    coverage: 'Most recent Form 4 lines in the snapshot',
  },
  {
    id: 'grades',
    board: 'street',
    title: 'Rating actions',
    represents: 'Sell-side rating changes: firm, previous rating, new rating, and whether it was a raise, cut, or maintain.',
    relatesToQxo:
      'The target price on the street board is a sentiment reading. This list is whether anyone changed their mind as the stock fell from the 2025 issuance prices. Maintains are not new information.',
    source: 'FMP grades',
    coverage: 'Recent actions in the snapshot',
  },
  {
    id: 'sec-filings',
    board: 'street',
    title: 'Recent SEC filings',
    represents: 'Forms filed with the SEC over roughly the last 400 days, with a link to the document.',
    relatesToQxo:
      'The next filings that change the model are the Q3 2026 10-Q (first TopBuild consolidation) and any S-3 or 424B (more equity). 8-Ks around deals are how the debt and preferred stack was updated.',
    source: 'FMP sec-filings-search',
    coverage: 'Last 400 days',
  },
];

export function getDataset(id: string): DatasetDef | undefined {
  return DATASETS.find((d) => d.id === id);
}

export function streetDataset(id: string, snapshot: StreetSnapshot | null): DatasetDef | undefined {
  const meta = STREET_META.find((d) => d.id === id);
  if (!meta) return undefined;
  return { ...meta, ...streetTable(id, snapshot) };
}

export function resolveDataset(id: string, snapshot: StreetSnapshot | null): DatasetDef | undefined {
  return getDataset(id) ?? streetDataset(id, snapshot);
}

export function datasetBoard(id: string): DatasetBoard | null {
  return getDataset(id)?.board ?? STREET_META.find((d) => d.id === id)?.board ?? null;
}

function streetTable(id: string, snapshot: StreetSnapshot | null): Pick<DatasetDef, 'columns' | 'rows'> {
  if (!snapshot) {
    return { columns: cols(['Status']), rows: [row(['Waiting for the FMP snapshot'])] };
  }
  if (id === 'annual-consensus') {
    return {
      columns: cols(['Year', 'Revenue avg', 'Revenue low', 'Revenue high', 'EPS avg', 'EPS low', 'EPS high', 'Analysts (rev / EPS)']),
      rows: snapshot.estimatesAnnual.map((e) =>
        row([
          yearLabel(e.date),
          e.revenueAvg == null ? '—' : money(Math.round(e.revenueAvg / 1e6)) + 'M',
          e.revenueLow == null ? '—' : money(Math.round(e.revenueLow / 1e6)) + 'M',
          e.revenueHigh == null ? '—' : money(Math.round(e.revenueHigh / 1e6)) + 'M',
          e.epsAvg == null ? '—' : e.epsAvg.toFixed(2),
          e.epsLow == null ? '—' : e.epsLow.toFixed(2),
          e.epsHigh == null ? '—' : e.epsHigh.toFixed(2),
          `${e.numAnalystsRevenue ?? '—'} / ${e.numAnalystsEps ?? '—'}`,
        ]),
      ),
    };
  }
  if (id === 'quarterly-consensus') {
    return {
      columns: cols(['Quarter', 'Period end', 'Revenue avg', 'Low', 'High', 'EPS avg', 'Analysts']),
      rows: snapshot.estimatesQuarterly.map((e) =>
        row([
          quarterLabel(e.date),
          e.date,
          e.revenueAvg == null ? '—' : money(Math.round(e.revenueAvg / 1e6)) + 'M',
          e.revenueLow == null ? '—' : money(Math.round(e.revenueLow / 1e6)) + 'M',
          e.revenueHigh == null ? '—' : money(Math.round(e.revenueHigh / 1e6)) + 'M',
          e.epsAvg == null ? '—' : e.epsAvg.toFixed(2),
          `${e.numAnalystsRevenue ?? '—'}`,
        ]),
      ),
    };
  }
  if (id === 'prints-vs-estimates') {
    return {
      columns: cols(['Reported', 'Revenue', 'Estimate', 'Surprise %', 'EPS', 'EPS est.', 'EPS Δ']),
      rows: snapshot.earnings.history.map((e) =>
        row([
          e.date,
          e.revenueActual == null ? '—' : money(Math.round(e.revenueActual / 1e6)) + 'M',
          e.revenueEstimated == null ? '—' : money(Math.round(e.revenueEstimated / 1e6)) + 'M',
          num(e.revenueSurprisePct),
          e.epsActual == null ? '—' : e.epsActual.toFixed(2),
          e.epsEstimated == null ? '—' : e.epsEstimated.toFixed(2),
          e.epsSurprise == null ? '—' : e.epsSurprise.toFixed(3),
        ]),
      ),
    };
  }
  if (id === 'ownership-13f') {
    const o = snapshot.ownership;
    return {
      columns: cols(['Field', 'Value'], ['left', 'right']),
      rows: [
        row(['Quarter end', o.date]),
        row(['Institutions holding', o.investorsHolding == null ? '—' : String(o.investorsHolding)]),
        row(['Change in holders', o.investorsHoldingChange == null ? '—' : String(o.investorsHoldingChange)]),
        row(['13F shares', o.numberOf13Fshares == null ? '—' : o.numberOf13Fshares.toLocaleString()]),
        row(['Share change', o.numberOf13FsharesChange == null ? '—' : o.numberOf13FsharesChange.toLocaleString()]),
        row(['New positions', o.newPositions == null ? '—' : String(o.newPositions)]),
        row(['Increased', o.increasedPositions == null ? '—' : String(o.increasedPositions)]),
        row(['Reduced', o.reducedPositions == null ? '—' : String(o.reducedPositions)]),
        row(['Closed', o.closedPositions == null ? '—' : String(o.closedPositions)]),
        row(['Calls reported', o.totalCalls == null ? '—' : o.totalCalls.toLocaleString()]),
        row(['Puts reported', o.totalPuts == null ? '—' : o.totalPuts.toLocaleString()]),
        row(['Put/call ratio', num(o.putCallRatio, 2)]),
      ],
    };
  }
  if (id === 'insiders') {
    return {
      columns: cols(['Date', 'Insider', 'Kind', 'Shares', 'Price'], ['left', 'left', 'left', 'right', 'right']),
      rows: snapshot.insiders.transactions.map((t) =>
        row([t.date, t.name, t.kind.replace(/_/g, ' '), t.shares == null ? '—' : t.shares.toLocaleString(), t.price ? t.price.toFixed(2) : '—']),
      ),
    };
  }
  if (id === 'grades') {
    return {
      columns: cols(['Date', 'Firm', 'Action', 'From', 'To'], ['left', 'left', 'left', 'left', 'left']),
      rows: snapshot.grades.map((g) => row([g.date, g.firm, g.action, g.previousGrade, g.newGrade])),
    };
  }
  return {
    columns: cols(['Filed', 'Form'], ['left', 'left']),
    rows: snapshot.filings.map((f) => row([f.date, f.form])),
  };
}
